import Analytics from '@/utils/analytics';
import EllipsisIcon from '@assets/icons/ellipsis.svg';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SettingIcon from '@assets/icons/settings.svg';
import SnapPeas from '@assets/icons/snap-peas-image.svg';
import { ACCOUNT_ORDERS_FIELDS_FRAGMENT } from './account-fragments';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  AllowedShortcutNumbers,
  AllowedShortcuts,
  KeyboardScope,
  numberKeyMap,
} from '@/platform/shortcuts/operator-shortcuts';
import {
  Animated,
  InteractionManager,
  NativeScrollEvent,
  Platform,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Button } from '@/components/button';
import { ChatStatus } from '@/enums';
import { ChatUnreadCountMap } from '@/screens/chats/hooks/use-chat-unread-count';
import { clampScrollOffset } from '@/utils/scrolling';
import { createStyles } from '@/contexts/theme-context';
import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import {
  focusedItem,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { gql } from '@apollo/client';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import {
  Props as ListItemProps,
  ListOption,
  SwipeableWithInstantClose,
} from '@/components/list-item';
import {
  ListItemType,
  RecyclerList,
  RecyclerListRef,
} from '@/components/recycler-list';
import {
  LocationBottomAction,
  LocationBottomActions,
} from './location-bottom-actions';
import {
  LocationPicker_AccountsQuery,
  LocationPicker_AccountsQueryVariables,
  LocationPickerAccountFieldsFragment,
} from '@/types';
import { LocationPickerListItemSubtitle } from './location-picker-list-item-subtitle';
import { LocationPickerRightAction } from './location-picker-right-action';
import { MoreActionsActionSheet } from './more-actions-action-sheet';
import { MultiStateCheckbox } from '@/components/multi-state-checkbox';
import { NewNoteActionSheet } from './new-note-action-sheet';
import { OfflineDataStatusMap } from '@/adapters/offline-mode';
import { QueryData } from '@/hooks/use-offline-data';
import { RemoveProspectConfirmationActionSheet } from './remove-prospect-confirmation-action-sheet';
import { SingleSelectFilterType } from '@/hooks/use-location-picker-filter-pills';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useDebouncedCallback } from 'use-debounce';
import { useKeyboardHotkeysContext } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSupplierRemoveRestaurant } from '@/hooks/use-supplier-remove-restaurant';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const ACCOUNTS_QUERY = gql`
  fragment LocationPickerAccountFields on chats {
    ...AccountOrdersFields
    restaurant_uuid
    supplier_uuid
    uuid
  }

  query LocationPicker_Accounts(
    $chatUUIDs: [uuid!]!
    $unplacedOrderStatuses: [String!]!
  ) {
    chats(where: { uuid: { _in: $chatUUIDs } }) {
      ...LocationPickerAccountFields
    }
  }

  ${ACCOUNT_ORDERS_FIELDS_FRAGMENT}
`;

export type LocationOption = ListOption<{
  hasUnseenNotifications: boolean;
  isSelected: boolean;
  onRemoveRestaurant?: (restaurantUUID: string) => void;
  relationship: EmployeeRestaurant;
}>;

const RIGHT_ACTION_BUTTON_WIDTH = 60;

const createChatAccountMapKey = (
  restaurantUUID: string,
  supplierUUID: string,
) => `${restaurantUUID}-${supplierUUID}`;

interface Props {
  accountModeFilter: SingleSelectFilterType | null;
  bottomActions: LocationBottomAction[];
  chatUnreadCountMap: ChatUnreadCountMap;
  contentContainerStyle?: StyleProp<ViewStyle>;
  dataStatus: QueryData<OfflineDataStatusMap>;
  hasMultipleSuppliers: boolean;
  isMultiSelecting: boolean;
  listRef: React.RefObject<RecyclerListRef<LocationOption> | null>;
  onCreateAccount: () => void;
  onRestaurantSelected: () => void;
  onViewAccount: (option: LocationOption) => void;
  restaurantRelationships: EmployeeRestaurant[];
  restaurantsWithNewInvoices: Set<string>;
  searching: boolean;
  selectAccount: (relationship: EmployeeRestaurant) => void;
  selectedAccountUUIDs: Set<string>;
}

export const LocationPickerList: React.FC<Props> = ({
  accountModeFilter,
  bottomActions,
  chatUnreadCountMap,
  contentContainerStyle,
  dataStatus,
  hasMultipleSuppliers,
  isMultiSelecting,
  listRef,
  onCreateAccount,
  onViewAccount,
  onRestaurantSelected,
  restaurantRelationships,
  restaurantsWithNewInvoices,
  searching,
  selectAccount,
  selectedAccountUUIDs,
}) => {
  const { bottom } = useSafeAreaInsets();
  const {
    canCreateAccount,
    supplierEmployeeRole,
    selectedRestaurantUUID,
    selectRestaurant,
  } = useAuthenticatedUserContext();
  const [styles, theme] = useStyles();
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const { disableScope, enableScope } = useKeyboardHotkeysContext();

  const isSupplierEmployee = Boolean(supplierEmployeeRole);
  const canAddLocations =
    canCreateAccount &&
    isSupplierEmployee &&
    !isMultiSelecting &&
    accountModeFilter !== SingleSelectFilterType.AvailableOffline;
  const [showNoteActionSheet, setShowNoteActionSheet] = useState(false);
  const [chatIDForNote, setChatIDForNote] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const swipeableItemRef = useRef<SwipeableWithInstantClose>(null);
  const closeSwipeable = useCallback(() => {
    swipeableItemRef.current?.close();
    swipeableItemRef.current = null;
  }, []);

  const [selectedRestaurant, setSelectedRestaurant] = useState<
    EmployeeRestaurant | null | undefined
  >();
  const [showMoreActionSheet, setShowMoreActionSheet] = useState(false);
  const [showRemoveProspectModal, setShowRemoveProspectModal] = useState(false);
  const [localRestaurantRelationships, setLocalRestaurantRelationships] =
    useState(restaurantRelationships);
  const [maxScrollDistance, setMaxScrollDistance] = useState(0);

  const hasScrolledToSelectedRef = useRef(false);
  const selectedScrollY = useRef(0);
  const scrollY = useRef(new Animated.Value(0)).current;

  const chatUUIDs = useMemo(
    () => restaurantRelationships.flatMap((r) => r.chatUUIDs),
    [restaurantRelationships],
  );
  const { data } = useWrappedQuery<
    LocationPicker_AccountsQuery,
    LocationPicker_AccountsQueryVariables
  >(ACCOUNTS_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !chatUUIDs.length,
    variables: {
      chatUUIDs,
      unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
    },
  });

  const chatAccountMap = useMemo(() => {
    return (data?.chats ?? []).reduce<{
      [accountKey: string]: LocationPickerAccountFieldsFragment;
    }>((result, chat) => {
      result[
        createChatAccountMapKey(chat.restaurant_uuid, chat.supplier_uuid)
      ] = chat;
      return result;
    }, {});
  }, [data?.chats]);

  useEffect(() => {
    setLocalRestaurantRelationships(restaurantRelationships);
  }, [restaurantRelationships]);

  useEffect(() => {
    if (focusedIndex !== null && listRef.current) {
      listRef.current.scrollToIndex({ animated: false, index: focusedIndex });
    }
  }, [focusedIndex, listRef]);

  /**
   * Disabling the SCOPED keyboard shortcuts, to allow for LOCATION_PICKER and GLOBAL keyboard shortcuts to work
   * This is done to allow for the user to navigate through the restaurant list using arrow keys
   * and to allow for the user to toggle global keyboard shortcuts
   */
  useEffect(() => {
    disableScope(KeyboardScope.SCOPED);
    enableScope(KeyboardScope.LOCATION_PICKER);
    enableScope(KeyboardScope.GLOBAL);
    return () => {
      disableScope(KeyboardScope.LOCATION_PICKER);
      enableScope(KeyboardScope.GLOBAL);
      enableScope(KeyboardScope.SCOPED);
    };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeLocalRestaurantRelationship = useCallback(
    (restaurantUUID: string) => {
      setLocalRestaurantRelationships((prev) => {
        return prev.filter(
          (restaurantRelationship) =>
            restaurantRelationship.uuid !== restaurantUUID,
        );
      });
    },
    [],
  );

  const onOpenNoteActionSheet = useCallback(
    (restaurantUUID: string, chatID: string) => {
      Analytics.track('location_picker.add_new_note_button', {
        restaurantUUID,
      });
      setChatIDForNote(chatID);
      setShowNoteActionSheet(true);
    },
    [],
  );

  const { showErrorToast } = useToasts();

  const { removeRestaurant, removeRestaurantError, removeRestaurantWithAlert } =
    useSupplierRemoveRestaurant();

  useEffect(() => {
    if (removeRestaurantError) {
      showErrorToast();
    }
  }, [removeRestaurantError, showErrorToast]);

  const onRemoveProspect = (
    restaurantName: string,
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    Analytics.track('location_picker.remove_prospect_button', {
      restaurantUUID,
      supplierUUID,
    });

    if (Platform.OS === 'web') {
      setShowRemoveProspectModal(true);
    } else {
      removeRestaurantWithAlert(
        restaurantUUID,
        restaurantName,
        supplierUUID,
        () => {
          removeLocalRestaurantRelationship(restaurantUUID);
        },
      );
    }
  };

  const sectionData = useMemo(() => {
    let data: LocationOption[] = localRestaurantRelationships
      .map((relationship) => ({
        metadata: {
          hasUnseenNotifications:
            relationship.chatUUIDs.some(
              (chatUUID) => chatUnreadCountMap[chatUUID] > 0,
            ) ||
            (!supplierEmployeeRole &&
              relationship.appPaymentsEnabled &&
              restaurantsWithNewInvoices.has(relationship.uuid)),
          isSelected: relationship.uuid === selectedRestaurantUUID,
          onRemoveRestaurant: removeLocalRestaurantRelationship,
          relationship,
        },
        subtitle: (
          <LocationPickerListItemSubtitle
            account={
              chatAccountMap[
                createChatAccountMapKey(
                  relationship.uuid,
                  relationship.supplierUUID,
                )
              ]
            }
            isSupplierUser={isSupplierEmployee}
            offlineDataStatus={dataStatus?.[relationship.uuid]}
            relationship={relationship}
            showSupplierName={isSupplierEmployee && hasMultipleSuppliers}
          />
        ),
        title: relationship.name,
        value: relationship.uuid,
      }))
      .sort((a, b) => {
        // Sort locations with unread notifications first
        return (
          Number(b.metadata?.hasUnseenNotifications) -
          Number(a.metadata?.hasUnseenNotifications)
        );
      });

    if (canAddLocations) {
      data.unshift({
        title: i18n.t('create_new_account'),
        value: ListItemType.Creatable,
      });
    }

    return data;
  }, [
    canAddLocations,
    chatAccountMap,
    chatUnreadCountMap,
    dataStatus,
    hasMultipleSuppliers,
    isSupplierEmployee,
    localRestaurantRelationships,
    removeLocalRestaurantRelationship,
    restaurantsWithNewInvoices,
    selectedRestaurantUUID,
    supplierEmployeeRole,
  ]);

  useEffect(() => {
    // Prevent auto scroll after user initiates search
    if (searching) {
      hasScrolledToSelectedRef.current = true;
    }
  }, [searching]);

  useEffect(() => {
    if (!listRef.current || hasScrolledToSelectedRef.current) {
      return;
    }

    const selectedIndex = sectionData.findIndex(
      (r) => r.value === selectedRestaurantUUID,
    );
    if (selectedIndex !== -1) {
      InteractionManager.runAfterInteractions(() => {
        listRef.current?.scrollToIndex({
          animated: false,
          index: selectedIndex,
        });
      });
    } else {
      hasScrolledToSelectedRef.current = true;
      scrollY.setValue(0);
    }
  }, [listRef, sectionData, selectedRestaurantUUID, scrollY]);

  // Enables floating action button animation after programmatic scroll
  // and resets offset after manual scrolls
  const debouncedMomentumScrollEnd = useDebouncedCallback(() => {
    if (!hasScrolledToSelectedRef.current) {
      hasScrolledToSelectedRef.current = true;
    } else {
      selectedScrollY.current = 0;
    }
  }, 300);

  const onScroll = useCallback(
    ({ nativeEvent }: { nativeEvent: NativeScrollEvent }) => {
      const offsetY = nativeEvent.contentOffset.y;
      const nextMaxScrollDistance =
        nativeEvent.contentSize.height - nativeEvent.layoutMeasurement.height;

      if (!maxScrollDistance && nextMaxScrollDistance > 0) {
        setMaxScrollDistance(nextMaxScrollDistance);
      }

      if (hasScrolledToSelectedRef.current) {
        // Use delta to avoid immediate jump after programmatic scroll
        scrollY.setValue(
          clampScrollOffset(
            offsetY - selectedScrollY.current,
            maxScrollDistance,
          ),
        );
      } else {
        selectedScrollY.current = offsetY;
      }
    },
    [maxScrollDistance, scrollY],
  );

  const listItemProps: Partial<ListItemProps<LocationOption>> = useMemo(
    () => ({
      leftComponentStyle: styles.leftComponentStyle,
      listItemStyle: (option, index) => [
        styles.listItem,
        option.value === ListItemType.Creatable && styles.creatableListItem,
        index === focusedIndex && focusedItem(-2, 4, 4),
      ],
      renderLeftComponent: (option) => {
        const visible = option.metadata?.hasUnseenNotifications;
        // style with backgroundColor instead of opacity to avoid bug that
        // causes the screen to go blank on mobile web
        return (
          <View
            style={[
              styles.unreadChatMessagesIndicator,
              visible ? styles.unreadChatMessagesIndicatorVisible : null,
            ]}
            testID={visible ? 'unread-indicator' : undefined}
          />
        );
      },
      renderListItemHeader: (option, index) =>
        option.metadata?.isSelected ? (
          <View
            style={[
              styles.selectedLocationBackground,
              index === focusedIndex ? focusedItem(-2, 4, 4) : null,
            ]}
          />
        ) : null,
      renderRightComponent: (option) => {
        if (!isSupplierEmployee) {
          return null;
        }

        if (isMultiSelecting) {
          return (
            <View style={styles.checkboxContainer}>
              <MultiStateCheckbox
                isPartiallySelected={false}
                isSelected={selectedAccountUUIDs.has(
                  option.metadata!.relationship.uuid,
                )}
                onSelect={() => selectAccount(option.metadata!.relationship)}
                trackingData={null}
              />
            </View>
          );
        }

        return Platform.OS === 'web' ? (
          <View style={styles.actionContainer}>
            <TouchableOpacity
              onPress={() => {
                onViewAccount(option);
                Analytics.track('location_picker.view_account_button', {
                  restaurantUUID: String(option.value),
                });
              }}
              style={styles.action}
              testID="view-account-button"
            >
              <SettingIcon
                height={21}
                stroke={theme.colors.primary}
                width={21}
              />
            </TouchableOpacity>
            {option.metadata?.relationship.chatStatus === ChatStatus.PROSPECT ||
            option.metadata?.relationship.showCrmActivities ? (
              <Button
                hoverStyle={{ backgroundColor: theme.colors.background }}
                onPress={() => {
                  setSelectedRestaurant(option.metadata?.relationship);
                  setShowMoreActionSheet(true);
                }}
                size="xsmall"
                style={[styles.action, styles.actionResetPadding]}
                testID="more-actions-button"
                trackingData={{
                  name: 'location_picker.more_actions_button',
                  properties: {
                    restaurantUUID: String(option.value),
                  },
                }}
                type="tertiary"
              >
                <EllipsisIcon
                  height={21}
                  stroke={theme.colors.primary}
                  width={21}
                />
              </Button>
            ) : null}
          </View>
        ) : null;
      },
      rightComponentStyle: styles.rightComponentStyle,
      swipeableProps:
        Platform.OS === 'web' || isMultiSelecting
          ? undefined
          : {
              onSwipeableRightWillOpen: (_, swipeableRef) => {
                if (
                  swipeableItemRef.current &&
                  swipeableItemRef.current !== swipeableRef
                ) {
                  closeSwipeable();
                }
                swipeableItemRef.current = swipeableRef;
              },
              renderRightActions: (option) => {
                if (option.value === ListItemType.Creatable) {
                  return;
                }

                const rightActions = [];
                if (isSupplierEmployee) {
                  rightActions.push(
                    <LocationPickerRightAction
                      backgroundColor={theme.colors.alertWarning}
                      Icon={SettingIcon}
                      iconProps={{
                        stroke: theme.colors.primary,
                      }}
                      key="view-account-button"
                      onPress={() => {
                        Analytics.track('location_picker.view_account_button', {
                          restaurantUUID: String(option.value),
                        });
                        closeSwipeable();
                        onViewAccount(option);
                      }}
                      text={i18n.t('manage_account')}
                    />,
                  );
                }

                if (
                  option.metadata?.relationship.chatStatus ===
                    ChatStatus.PROSPECT ||
                  option.metadata?.relationship.showCrmActivities
                ) {
                  rightActions.push(
                    <LocationPickerRightAction
                      backgroundColor={theme.colors.background}
                      Icon={EllipsisIcon}
                      iconProps={{
                        stroke: theme.colors.primary,
                      }}
                      key="more-actions-button"
                      onPress={() => {
                        Analytics.track('location_picker.more_actions_button', {
                          restaurantUUID: String(option.value),
                        });
                        setSelectedRestaurant(option.metadata?.relationship);
                        setShowMoreActionSheet(true);
                        closeSwipeable();
                      }}
                      text={i18n.t('more')}
                    />,
                  );
                }

                return rightActions.length > 0 ? (
                  <View
                    style={[
                      styles.rightActionContainer,
                      {
                        width: rightActions.length * RIGHT_ACTION_BUTTON_WIDTH,
                      },
                    ]}
                  >
                    {rightActions}
                  </View>
                ) : null;
              },
            },
    }),
    [
      closeSwipeable,
      focusedIndex,
      isMultiSelecting,
      isSupplierEmployee,
      selectedAccountUUIDs,
      selectAccount,
      onViewAccount,
      styles,
      theme,
    ],
  );

  const selectedRestaurantIndex = useMemo(() => {
    return sectionData.findIndex((r) => r.value === selectedRestaurantUUID);
  }, [sectionData, selectedRestaurantUUID]);

  const sectionDataLength = sectionData.length;

  const onSelect = useCallback(
    (option: LocationOption) => {
      if (option.value === ListItemType.Creatable) {
        onCreateAccount();
      } else if (isMultiSelecting) {
        selectAccount(option.metadata!.relationship);
      } else {
        selectRestaurant(String(option.value));
        Analytics.track('location_picker.select_account', {
          restaurantUUID: String(option.value),
        });
        onRestaurantSelected();
      }
    },
    [
      isMultiSelecting,
      onCreateAccount,
      onRestaurantSelected,
      selectRestaurant,
      selectAccount,
    ],
  );

  const handleAddNoteForFocusedItem = useCallback(() => {
    if (focusedIndex !== null) {
      const option = sectionData[focusedIndex];
      if (
        option.value !== ListItemType.Creatable &&
        option.metadata?.relationship &&
        option.metadata.relationship.chatUUIDs.length > 0
      ) {
        const restaurantUUID = option.metadata.relationship.uuid;
        const chatID = option.metadata.relationship.chatUUIDs[0]; // Assuming the first chat ID
        onOpenNoteActionSheet(restaurantUUID, chatID);
      }
    }
  }, [focusedIndex, onOpenNoteActionSheet, sectionData]);

  const handleViewAccountForFocusedItem = useCallback(() => {
    if (focusedIndex !== null) {
      const option = sectionData[focusedIndex];
      if (option.value !== ListItemType.Creatable && isSupplierEmployee) {
        onViewAccount(option);
      }
    }
  }, [focusedIndex, isSupplierEmployee, onViewAccount, sectionData]);

  const handleSelectFocusedItem = useCallback(() => {
    if (focusedIndex !== null) {
      const option = sectionData[focusedIndex];
      onSelect(option);
    }
  }, [focusedIndex, onSelect, sectionData]);

  const moveFocusUp = useCallback(() => {
    setFocusedIndex((prevIndex) => {
      if (prevIndex === null) {
        if (selectedRestaurantIndex !== -1) {
          return selectedRestaurantIndex;
        }
        return sectionDataLength - 1;
      } else if (prevIndex <= 0) {
        return sectionDataLength - 1;
      }
      return prevIndex - 1;
    });
  }, [sectionDataLength, selectedRestaurantIndex]);

  const moveFocusDown = useCallback(() => {
    setFocusedIndex((prevIndex) => {
      if (prevIndex === null) {
        if (selectedRestaurantIndex !== -1) {
          return selectedRestaurantIndex;
        }
        return 0;
      } else if (prevIndex >= sectionDataLength - 1) {
        return 0;
      }
      return prevIndex + 1;
    });
  }, [sectionDataLength, selectedRestaurantIndex]);

  const focusItemAtIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < sectionDataLength) {
        setFocusedIndex(index);
      }
    },
    [sectionDataLength],
  );

  useKeyboardShortcuts(
    AllowedShortcuts.AddNote,
    handleAddNoteForFocusedItem,
    [handleAddNoteForFocusedItem],
    {},
    true,
    KeyboardScope.LOCATION_PICKER,
  );

  useKeyboardShortcuts(
    AllowedShortcuts.ViewRestaurantAccount,
    handleViewAccountForFocusedItem,
    [handleViewAccountForFocusedItem],
    {},
    true,
    KeyboardScope.LOCATION_PICKER,
  );

  useKeyboardShortcuts(
    // so this isn't necessarily a KeyboardShortcut, but it is an action that we want to handle
    // in the event that a user focused on a list item and presses 'Enter'
    [Key.Enter],
    handleSelectFocusedItem,
    [handleSelectFocusedItem],
    {},
    true,
    KeyboardScope.LOCATION_PICKER,
  );

  useKeyboardShortcuts(
    AllowedShortcuts.ArrowUp,
    moveFocusUp,
    [moveFocusUp],
    {},
    true,
    KeyboardScope.LOCATION_PICKER,
  );

  useKeyboardShortcuts(
    AllowedShortcuts.ArrowDown,
    moveFocusDown,
    [moveFocusDown],
    {},
    true,
    KeyboardScope.LOCATION_PICKER,
  );

  useKeyboardShortcuts(
    AllowedShortcutNumbers,
    (_, handler) => {
      const key = handler.keys?.join().toLowerCase();
      const numberPressed = numberKeyMap[`${Key.Alt}+${key}`];
      if (numberPressed !== undefined) {
        const index = numberPressed - 1;
        focusItemAtIndex(index);
      }
    },
    [focusItemAtIndex],
    {},
    true,
    KeyboardScope.LOCATION_PICKER,
  );

  const isViewingOfflineAccounts =
    isOfflineModeEnabled &&
    accountModeFilter === SingleSelectFilterType.AvailableOffline;
  const isListEmpty = isViewingOfflineAccounts
    ? sectionData.length === 0
    : sectionData.length === 0 || (canAddLocations && sectionData.length === 1);
  const showBottomActions = bottomActions.length > 0 && !isMultiSelecting;

  return searching ? (
    <View style={styles.loadingContainer}>
      <ActivityIndicator />
    </View>
  ) : (
    <>
      <RecyclerList
        contentContainerStyle={[
          styles.recyclerContent,
          { paddingBottom: bottom },
          contentContainerStyle,
        ]}
        estimatedItemSize={100}
        keyboardDismissMode={Platform.OS === 'web' ? 'none' : 'on-drag'}
        keyboardShouldPersistTaps="handled"
        ListFooterComponent={
          isListEmpty ? (
            <EmptyStateMinimal
              containerStyle={styles.emptyState}
              contentStyle={styles.emptyStateContent}
              iconProps={{
                accentFill: theme.colors.accent,
                fill: theme.colors.primary,
              }}
              ImageComponent={SnapPeas}
              text={i18n.t('oh_snap_peas')}
              textStyle={[TYPOGRAPHY.h2, styles.emptyStateTitle]}
            >
              <Text style={TYPOGRAPHY.medium}>
                {i18n.t('no_results_found')}
              </Text>
            </EmptyStateMinimal>
          ) : null
        }
        listItemProps={listItemProps}
        listItemSeparatorProps={{
          style: styles.listItemSeparator,
        }}
        listRef={listRef}
        onMomentumScrollEnd={debouncedMomentumScrollEnd}
        onScroll={onScroll}
        onSelect={(option) => onSelect(option)}
        renderListHeaderAsListItem
        scrollEventThrottle={16}
        sections={[
          {
            data: sectionData,
            key: null,
          },
        ]}
        stickyHeaderHiddenOnScroll
      />
      {showNoteActionSheet && chatIDForNote ? (
        <NewNoteActionSheet
          chatUUID={chatIDForNote}
          onClose={() => {
            setShowNoteActionSheet(false);
            setShowMoreActionSheet(false);
          }}
        />
      ) : null}
      {showMoreActionSheet && selectedRestaurant ? (
        <MoreActionsActionSheet
          onClose={() => setShowMoreActionSheet(false)}
          onOpenNoteActionSheet={() => {
            setShowMoreActionSheet(false);
            onOpenNoteActionSheet(
              selectedRestaurant?.uuid,
              selectedRestaurant.chatUUIDs[0],
            );
          }}
          onRemoveProspect={() => {
            setShowMoreActionSheet(false);
            onRemoveProspect(
              selectedRestaurant.name,
              selectedRestaurant.uuid,
              selectedRestaurant.supplierUUID,
            );
          }}
          selectedRestaurant={selectedRestaurant}
        />
      ) : null}
      {showRemoveProspectModal && selectedRestaurant ? (
        <RemoveProspectConfirmationActionSheet
          onCancel={() => setShowRemoveProspectModal(false)}
          onConfirm={async () => {
            setShowRemoveProspectModal(false);
            removeLocalRestaurantRelationship(selectedRestaurant.uuid);
            await removeRestaurant(
              selectedRestaurant.uuid,
              selectedRestaurant.supplierUUID,
            );
          }}
          restaurantName={selectedRestaurant.name}
        />
      ) : null}
      {showBottomActions ? (
        <LocationBottomActions
          bottomActions={bottomActions}
          maxScrollDistance={maxScrollDistance}
          scrollY={scrollY}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  action: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: 8,
  },
  actionContainer: {
    flexDirection: 'row',
  },
  actionResetPadding: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  checkboxContainer: {
    marginRight: LIST_MARGIN_HORIZONTAL,
  },
  creatableListItem: {
    marginLeft: LIST_MARGIN_HORIZONTAL,
  },
  emptyState: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    paddingTop: SCREEN_PADDING,
  },
  emptyStateContent: {
    height: '100%',
    justifyContent: 'center',
  },
  emptyStateTitle: {
    marginBottom: 8,
  },
  leftComponentStyle: {
    marginRight: 8,
  },
  listItem: {
    paddingHorizontal: 8,
  },
  listItemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: 0,
  },
  loadingContainer: {
    marginTop: SCREEN_PADDING,
  },
  recyclerContent: {
    paddingTop: 8,
  },
  rightActionContainer: {
    flexDirection: 'row',
  },
  rightComponentStyle: {
    marginLeft: 8,
  },
  selectedLocationBackground: {
    backgroundColor: theme.colors.accentLight,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: -1,
  },
  unreadChatMessagesIndicator: {
    backgroundColor: theme.colors.transparent,
    borderRadius: 6,
    height: 6,
    marginHorizontal: 2,
    width: 6,
  },
  unreadChatMessagesIndicatorVisible: {
    backgroundColor: theme.sk.color.icon.positive.secondary,
  },
}));
